/**
 * "Delete forever" on one Bin entry (#1167 S2): its instance of the generic job
 * machinery, and the run end to end. How a job is followed — socket first,
 * status route as the safety net — is in `job-tracker.ts` (deadline:
 * `JOB_DEADLINE_MS`, shared with the worker's pickup check).
 *
 * A small entry answers inline (200), as before. A big one is queued as the
 * `bin-purge-item` job (202) and followed to its end.
 */
import type { OperationProgressEvent } from '@papan/realtime/client'
import {
  createJobTracker, requestOutcomeUnknown, JobLostError,
  type JobTracker, type JobTrackerDeps, type JobTrackerTimings, type JobStatus,
} from './job-tracker.js'

/** A completed Delete forever: the row is gone. The status route's answer carries the
 *  counts; the completed event carries nothing (an empty outcome still ends the follow). */
export interface BinPurgeJobOutcome {
  deletedFiles?: number
  blobsRemoved?: number
}

export type BinPurgeTrackerDeps = Omit<JobTrackerDeps<BinPurgeJobOutcome>, 'kind' | 'readResult'>

export type BinPurgeTracker = JobTracker<BinPurgeJobOutcome>

function readBinPurgeResult(_e: OperationProgressEvent): BinPurgeJobOutcome {
  return {}
}

export function createBinPurgeTracker(
  deps: BinPurgeTrackerDeps,
  ownOperationId: string,
  timings: JobTrackerTimings = {},
): BinPurgeTracker {
  return createJobTracker({ ...deps, kind: 'bin-purge', readResult: readBinPurgeResult }, ownOperationId, timings)
}

/** What `filesApi.deleteBinItemAsync` answered. */
export type BinPurgeStartResult =
  | { kind: 'done' }
  | { kind: 'queued'; jobId: string; operationId: string }

/** `GET /api/bin/purge-status/:jobId`. `failed` is a 200. */
export type BinPurgeJobStatus = JobStatus<BinPurgeJobOutcome>

export interface BinPurgeRunDeps {
  /** Ask for the Delete forever (`filesApi.deleteBinItemAsync`). */
  start: (binId: string, operationId: string) => Promise<BinPurgeStartResult>
  getStatus: (jobId: string) => Promise<BinPurgeJobStatus>
  /** The tab's realtime socket; absent ⇒ the status route is polled instead. */
  onOperationEvent?: BinPurgeTrackerDeps['onOperationEvent']
  isConnected?: () => boolean
  /** Tests make the tracker fast. */
  timings?: JobTrackerTimings
}

export interface BinPurgeRunHooks {
  /** The purge was queued (or joined) as a job: the caller shows its progress. */
  onQueued?: (job: { jobId: string; operationId: string }) => void
}

/**
 * A person's Delete forever on one Bin entry, end to end: the request, then — for
 * a big entry — the background job it queued. Resolves `'inline'` or `'job'` once
 * the entry is gone; rejects with `JobFailedError` (the server's words: refused at
 * pickup, expired before it ran …), `JobLostError` (outcome unknown — refresh),
 * or the request's own error (a 409 "being deleted" by someone else, a 403…).
 */
export async function runBinPurgeJob(binId: string, deps: BinPurgeRunDeps, hooks: BinPurgeRunHooks = {}): Promise<'inline' | 'job'> {
  const operationId = `bin-purge-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const tracker = createBinPurgeTracker({
    onOperationEvent: deps.onOperationEvent ?? (() => () => {}),
    isConnected: deps.isConnected ?? (() => false),
    getStatus: deps.getStatus,
  }, operationId, deps.timings)
  let started: BinPurgeStartResult
  try {
    try {
      started = await deps.start(binId, operationId)
    } catch (err) {
      if (!requestOutcomeUnknown(err)) throw err
      // No answer arrived, so the server may have acted anyway. Asking again is
      // safe: the same person's second Delete forever joins the running job; an
      // inline purge that already finished answers 404.
      try {
        started = await deps.start(binId, operationId)
      } catch (retryErr) {
        if (requestOutcomeUnknown(retryErr) || (retryErr as { status?: number })?.status === 404) throw new JobLostError()
        throw retryErr
      }
    }
  } catch (err) {
    tracker.dispose()
    throw err
  }
  if (started.kind === 'done') {
    tracker.dispose()
    return 'inline'
  }
  hooks.onQueued?.({ jobId: started.jobId, operationId: started.operationId })
  await tracker.follow(started.jobId, started.operationId)
  return 'job'
}
